// tslint:disable:max-file-line-count
import {
  IAgentSchema,
  IOfferSchema,
  IUserSchema,
  IWorkTimeInfoSchema,
  IValueAddedServicesSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { parseCookies } from '@cian/utils';

import { AccountType, FlatType } from '../store/offer';
import { isFromBuilder, isOfficialRepresentative, isFromBuilderPresenter } from './agent';
import { FDealType, getDealType, isRent } from './deal_type';
import { FOfferType, getOfferType, isCommercial, isFlat, isSuburban, isUrban } from './offer_type';
import { isSuburbanOfferFromBuilder } from './suburban';

export interface ISoprUser {
  abGroup?: number;
  balance?: number | null;
  cianUserId?: number;
  email?: string;
  numberOfObjects?: number;
  paymentType?: string;
  registrationDate?: number;
  type?: string | null;
  userId?: number;
}

export type TObjectType =
  'flat_old' |
  'flat_new' |
  'flat_new' |
  'flat_new_dev' |
  'flat_old' |
  'commercial' |
  'suburban' |
  'suburban_dev' |
  'JK' |
  'BC' |
  'TC';
export type TEventPropertiesPageDealType =
  'rent_day' |
  'sale' |
  'rent_long';
export interface ISoprPage {
  breadCrumbs?: string[];
  customPageURL?: string;
  dealType?: TEventPropertiesPageDealType;
  fromDeveloper?: number;
  newObjects?: number;
  objectType?: TObjectType;
  offerID?: number;
  offerPhone?: string;
  offersQty?: string;
  pageType: 'Card';
  queryString?: string;
  region?: string;
  siteType: 'desktop';
}

export interface ISoprPartner {
  CriteoEmail?: string;
  CriteoNewCustomer?: string;
  CriteoProducts?: number[];
  FB_City?: string;
  FB_Region?: string;
}

export type TVariant =
  'paid' |
  'free' |
  'premium' |
  'top3' |
  'color' |
  'auction';
export interface ISoprProducts {
  brand: string;
  category?: string | undefined;
  cianId: number;
  dealType: TEventPropertiesPageDealType;
  headline?: boolean;
  hiddenBase: boolean;
  id: number;
  name?: string;
  nv?: boolean;
  objectType: TObjectType;
  owner?: boolean;
  ownerCianId: number | undefined;
  ownerId: number | undefined;
  parentId?: number | undefined;
  photosCount?: number;
  podSnos?: boolean;
  position: number;
  price?: number;
  published: boolean;
  quantity?: number;
  repres?: boolean;
  variant: TVariant;
  consultant: boolean;
}

export type TTrackingBuildingType = 'BC' | 'TC' | 'JK' | 'KP';
export const TrackingBuildingType = {
  Bc: 'BC' as TTrackingBuildingType,
  Jk: 'JK' as TTrackingBuildingType,
  Kp: 'KP' as TTrackingBuildingType,
  Tc: 'TC' as TTrackingBuildingType,
};

export interface ITrackingOptions {
  buildingType?: TTrackingBuildingType;
}

export function getCustomUrl(offer: IOfferSchema, agent: IAgentSchema | null, options?: ITrackingOptions): string {
  const dealType = getDealType(offer);
  const offerType = getOfferType(offer);
  const params: Array<string | undefined | boolean | number> = [];
  const isReliable = offer.newbuilding && offer.newbuilding.showJkReliableFlag;

  params.push(isRent(dealType) ? 'rent' : 'sale');
  params.push(getTrackingOfferType(offerType));

  if (options) {
    params.push(options.buildingType);
  }

  if (offer.trackingData) {
    params.push(offer.trackingData.moId !== null && `mo_id=${offer.trackingData.moId}`);
    params.push(offer.trackingData.oblId !== null && `obl_id=${offer.trackingData.oblId}`);
    params.push(offer.trackingData.cityId !== null && `city_id=${offer.trackingData.cityId}`);
  }

  params.push(getTrackingObjectType(offerType, dealType));
  params.push(getTrackingObjType(offer));
  params.push(getTrackingRentType(dealType));
  params.push(getTrackingAgentType(agent));
  params.push(offer.cianId);
  params.push(`from_developer=${
    isFromBuilder(offer) || isFromBuilderPresenter(offer) || isSuburbanOfferFromBuilder(offer)
      ? 1
      : 0
  }`);
  params.push(`repres=${isOfficialRepresentative(offer) ? 1 : 0}`);
  params.push(`owner=${offer.isByHomeowner ? 1 : 0}`);
  params.push(`pod_snos=${offer.isDemolishedInMoscowProgramm ? 1 : 0}`);
  params.push(`nv=${isReliable ? 1 : 0}`);

  return `/${params.filter(Boolean).join('/')}/`;
}

/* istanbul ignore next */
function getTrackingObjectType(offerType: FOfferType, dealType: FDealType): string {
  if (dealType === FDealType.Sale) {
    if (offerType === FOfferType.FlatNew) {
      return 'object_type=2';
    } else {
      return 'object_type=1';
    }
  }

  return 'object_type=';
}

/* istanbul ignore next */
function getTrackingOfferType(offerType: FOfferType): string | undefined {
  if (isUrban(offerType)) {
    return 'flat';
  }

  if (isSuburban(offerType)) {
    return 'suburban';
  }

  if (isCommercial(offerType)) {
    return 'commercial';
  }

  return undefined;
}

/* istanbul ignore next */
function getTrackingRentType(dealType: FDealType): string | undefined {
  if (dealType === FDealType.RentDaily) {
    return 'rent_type=daily';
  }

  if (dealType === FDealType.RentLongterm) {
    return 'rent_type=long';
  }

  return undefined;
}

/* istanbul ignore next */
function getTrackingAgentType(agent: IAgentSchema | null): string {
  if (agent && agent.accountType === AccountType.Agency) {
    return 'spec=company';
  }

  if (agent && agent.accountType === AccountType.Specialist) {
    return 'spec=agent';
  }

  return 'spec=none';
}

/* istanbul ignore next */
function getTrackingObjType(offer: IOfferSchema): string | undefined {
  const offerType = getOfferType(offer);

  if (offerType === FOfferType.Bed) {
    return 'ga_obj_type=10';
  }

  if (offerType === FOfferType.Room) {
    return 'ga_obj_type=0';
  }

  if (isFlat(offerType)) {
    if (offer.flatType === FlatType.OpenPlan) {
      return 'ga_obj_type=7';
    }

    if (offer.flatType === FlatType.Studio) {
      return 'ga_obj_type=9';
    }

    if (offer.roomsCount > 0 && offer.roomsCount < 6) {
      return `ga_obj_type=${offer.roomsCount}`;
    }

    if (offer.roomsCount >= 6) {
      return 'ga_obj_type=6';
    }
  }

  if (isSuburban(offerType)) {
    if (offerType === FOfferType.House) {
      return 'ga_obj_type=1';
    }

    if (offerType === FOfferType.HousePart) {
      return 'ga_obj_type=2';
    }

    if (offerType === FOfferType.Land) {
      return 'ga_obj_type=3';
    }

    if (offerType === FOfferType.Townhouse) {
      return 'ga_obj_type=4';
    }
  }

  if (isCommercial(offerType)) {
    switch (offerType) {
      case FOfferType.Office:
        return 'ga_obj_type=1';
      case FOfferType.TradeArea:
        return 'ga_obj_type=2';
      case FOfferType.Warehouse:
        return 'ga_obj_type=3';
      case FOfferType.PublicCatering:
        return 'ga_obj_type=4';
      case FOfferType.FreeAppointmentObject:
        return 'ga_obj_type=5';
      case FOfferType.Garage:
        return 'ga_obj_type=6';
      case FOfferType.Manufacture:
        return 'ga_obj_type=7';
      case FOfferType.AutoService:
        return 'ga_obj_type=9';
      case FOfferType.Business:
        return 'ga_obj_type=10';
      case FOfferType.Building:
        return 'ga_obj_type=11';
      case FOfferType.DomesticServices:
        return 'ga_obj_type=12';
      case FOfferType.CommercialLand:
        return 'ga_obj_type=13';
      default:
        return undefined;
    }
  }

  return undefined;
}

/* istanbul ignore next */
export const getBrand = (agent: IAgentSchema | null): string => {
  if (agent && agent.realtyUserId && agent.userType) {
    return `${agent.realtyUserId}/${agent.userType}`;
  }

  return '';
};

export const getCategory = (offer: IOfferSchema): string | undefined => {
  const offerType = getOfferType(offer);

  let offerTypeStr;
  if (offerType === FOfferType.FlatOld || offerType === FOfferType.Room) {
    offerTypeStr = 'flat_old';
  } else if (offerType === FOfferType.FlatNew && (isFromBuilder(offer) || isFromBuilderPresenter(offer))) {
    offerTypeStr = 'flat_new_dev';
  } else if (offerType === FOfferType.FlatNew) {
    offerTypeStr = 'flat_new';
  } else if (offerType & FOfferType.Commercial) {
    offerTypeStr = 'commercial';
  } else if (offerType & FOfferType.Suburban && isSuburbanOfferFromBuilder(offer)) {
    offerTypeStr = 'suburban_dev';
  } else if (offerType & FOfferType.Suburban) {
    offerTypeStr = 'suburban';
  }

  return offerTypeStr;
};

/* istanbul ignore next */
export function getProduct(offer: IOfferSchema, agent: IAgentSchema | null) {
  return {
    brand: getBrand(agent),
    category: getCategory(offer),
    id: offer.cianId,
    price: offer.bargainTerms.prices.rur,
    quantity: 1,
    variant: getVariants(offer),
  };
}

/* istanbul ignore next */
export const getParentName = (offer: IOfferSchema): string | null => {
  const parentName = offer.newbuilding && offer.newbuilding.name ||
    offer.businessShoppingCenter && offer.businessShoppingCenter.parentName;

  return parentName || null;
};

/* istanbul ignore next */
export const getParentId = (offer: IOfferSchema, kpId: number | null): number | undefined => {
  const offerType = getOfferType(offer);

  if (offer.newbuilding && offer.newbuilding.id && !isSuburban(offerType)) {
    return Number(offer.newbuilding.id);
  }

  if (offer.businessShoppingCenter && offer.businessShoppingCenter.parentId && !isSuburban(offerType)) {
    return offer.businessShoppingCenter.parentId;
  }

  return kpId || undefined;
};

/* istanbul ignore next */
export function getAction(defaultAction: string, workTimeInfo?: IWorkTimeInfoSchema) {
  if (workTimeInfo) {
    return workTimeInfo.isAvailable ? defaultAction : `${defaultAction}_inactive`;
  } else {
    return defaultAction;
  }
}

/* istanbul ignore next */
export function getUser(user: IUserSchema | null, abGroup: number): ISoprUser {
  return {
    abGroup,
    cianUserId: user && user.cianUserId ? Number(user.cianUserId) : undefined,
    email: user && user.email ? user.email : undefined,
    type: user && user.ga && user.ga.type
      ? user.ga.type || 'not_authorized'
      : 'not_authorized',
    userId: user && user.realtyUserId ? Number(user.realtyUserId) : undefined,
  };
}

/* istanbul ignore next */
export function getPage(
  agent: IAgentSchema | null,
  offer: IOfferSchema,
): ISoprPage {
  return {
    customPageURL: getCustomUrl(offer, agent),
    dealType: getDealTypeSopr(offer),
    objectType: getCategory(offer) as TObjectType,
    offerID: offer.cianId,
    offerPhone: offer.phones && offer.phones.length > 0
      ? `${offer.phones[0].countryCode}${offer.phones[0].number}`
      : '',
    pageType: 'Card',
    region: (offer.trackingData &&
      offer.trackingData.moId !== null &&
      offer.trackingData.oblId !== null &&
      offer.trackingData.cityId !== null)
        ? `${offer.trackingData.moId}-${offer.trackingData.oblId}-${offer.trackingData.cityId}`
        : '1',
    siteType: 'desktop',
  };
}

/* istanbul ignore next */
export function getPartner(
  user: IUserSchema | null,
  offer: IOfferSchema,
): ISoprPartner {
  const cookies = parseCookies(document.cookie);

  return {
    CriteoEmail: user && user.ga && user.ga.emailMd5 ? user.ga.emailMd5 : '',
    CriteoNewCustomer: !cookies['_CIAN_GK'] ? '1' : '0',
    CriteoProducts: [],
    FB_City: offer.trackingData && offer.trackingData.fbCity
      ? offer.trackingData.fbCity
      : '',
    FB_Region: offer.trackingData && offer.trackingData.fbRegion
      ? offer.trackingData.fbRegion
      : '',
  };
}

/* istanbul ignore next */
export const getHeadline = (offer: IOfferSchema): boolean => {
  const { title, valueAddedServices } = offer;

  if (!valueAddedServices) {
    return false;
  }

  return !!((valueAddedServices.isTop3 || valueAddedServices.isPremium)
    && typeof title === 'string' && title.trim().length);
};

/* istanbul ignore next */
export function getDealTypeSopr(offer: IOfferSchema): TEventPropertiesPageDealType {
  const dealType = getDealType(offer);

  const mapDealType: { [key: number]: TEventPropertiesPageDealType } = {
    [FDealType.RentDaily]: 'rent_day',
    [FDealType.RentLongterm]: 'rent_long',
    [FDealType.Sale]: 'sale',
  };

  return mapDealType[dealType];
}

/* istanbul ignore next */
export function getVAS(valueAddedServices: IValueAddedServicesSchema | null): EBC.TVas[] {
  if (!valueAddedServices) {
    return [];
  }

  const {
    isAuction,
    isColorized,
    isFree,
    isPaid,
    isPremium,
    isTop3,
  } = valueAddedServices;

  return [
    isAuction && 'auction',
    isColorized && 'color',
    isFree && 'free',
    isPaid && 'paid',
    isPremium && 'premium',
    isTop3 && 'top3',
  ].filter(Boolean) as EBC.TVas[];
}

export function getVariants(offer: IOfferSchema): string {
  if (offer.valueAddedServices) {
    const variants = Object.keys(offer.valueAddedServices)
      .reduce((acc, vasKey) => {
      // tslint:disable:no-any
      if ((offer.valueAddedServices as any)[vasKey]) {
        switch (vasKey) {
          case 'isFree':
            acc.push('free');
            break;
          case 'isColorized':
            acc.push('color');
            break;
          case 'isPaid':
            acc.push('paid');
            break;
          case 'isPremium':
            acc.push('premium');
            break;
          case 'isTop3':
            acc.push('top3');
            break;
          case 'isAuction':
            acc.push('auction');
            break;
          default:
            break;
        }
      }
      // tslint:enable:no-any

      return acc;
    }, [] as string[]);

    /**
     * Это эксперимент для слоно-топа
     */
    //TODO не забыть удалить, если неудачно закончится
    // tslint:disable-next-line:no-any
    if ((offer as any).isSlonoTop) {
      variants.push('slonotop');
    }

    return variants.join('/');
  }

  return '';
}
